import React, { useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import "@/index.css";
import { Layout } from "./components/Layout";
import HomePage from "./pages/HomePage";
import DienstenPage from "./pages/DienstenPage";
import ServicePage from "./pages/ServicePage";
import ServiceCityPage from "./pages/ServiceCityPage";
import LocatiesPage from "./pages/LocatiesPage";
import LocationPage from "./pages/LocationPage";
import ProjectenPage, { ProjectDetailPage } from "./pages/ProjectenPage";
import OverOnsPage from "./pages/OverOnsPage";
import ReviewsPage from "./pages/ReviewsPage";
import FaqPage from "./pages/FaqPage";
import ContactPage from "./pages/ContactPage";
import OffertePage from "./pages/OffertePage";

// Heavy / infrequently-visited routes are code-split so they don't ship in the
// main bundle. Public bundle is what mobile visitors actually download.
// asyncPage: hydratie-veilig alternatief voor React.lazy — na preload rendert
// het component synchroon, waardoor prerendered HTML zonder Suspense-mismatch
// hydrateert. Zonder preload (client-navigatie) laadt de chunk via useEffect.
function asyncPage(load) {
  let Comp = null;
  const Async = (props) => {
    const [, force] = React.useReducer((x) => x + 1, 0);
    useEffect(() => {
      if (!Comp) load().then((m) => { Comp = m.default; force(); });
    }, []);
    return Comp ? <Comp {...props} /> : <RouteFallback />;
  };
  Async.preload = () => load().then((m) => { Comp = m.default; });
  return Async;
}

const GratisChecklistPage = asyncPage(() => import(/* webpackChunkName: "public-checklist" */ "./pages/GratisChecklistPage"));
const IsolatieCombiPage   = asyncPage(() => import(/* webpackChunkName: "public-isolatie" */ "./pages/IsolatieCombiPage"));
const ConfiguratorPage    = asyncPage(() => import(/* webpackChunkName: "public-configurator" */ "./pages/ConfiguratorPage"));
const KennisbankPage      = asyncPage(() => import(/* webpackChunkName: "public-kennisbank" */ "./pages/KennisbankPage"));
const KennisbankArticlePage = asyncPage(() => import(/* webpackChunkName: "public-kennisbank" */ "./pages/KennisbankPage").then((m) => ({ default: m.KennisbankArticlePage })));

// Preload de chunk van de huidige route vóór hydratie, zodat de eerste render
// synchroon het echte component toont (geen Suspense-mismatch, geen flash).
const ASYNC_ROUTES = [
  ["/gratis-checklist", GratisChecklistPage],
  ["/isolatie-combi", IsolatieCombiPage],
  ["/configurator", ConfiguratorPage],
  ["/kennisbank", KennisbankPage],
];
export function preloadRoute(pathname) {
  const clean = (pathname || "/").replace(/\/+$/, "") || "/";
  if (clean.startsWith("/kennisbank/")) return KennisbankArticlePage.preload();
  const hit = ASYNC_ROUTES.find(([p]) => clean === p);
  return hit ? hit[1].preload() : Promise.resolve();
}

// Admin surface — never loaded for public visitors. All admin pages share one
// webpack chunk so navigating between Admin routes is instant after login.
const LoginPage             = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/LoginPage"));
const AdminDashboardPage    = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminDashboardPage"));
const AdminLeadsPage        = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminLeadsPage"));
const AdminProjectsPage     = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminProjectsPage"));
const AdminProjectEditPage  = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminProjectEditPage"));
const AdminSettingsPage     = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminSettingsPage"));

import { AuthProvider } from "./context/AuthContext";
import { ConsentProvider } from "./context/ConsentContext";
import { CookieConsent } from "./components/CookieConsent";
import { ProtectedRoute } from "./components/ProtectedRoute";
import NotFoundPage from "./pages/NotFoundPage";
import { PrivacyPage, VoorwaardenPage } from "./pages/LegalPages";
import { captureUtmFromLocation } from "./utils/utm";
import { trackPageView } from "./utils/analytics";
import { initAutofillDetector } from "./utils/autofillDetector";

// Minimal, non-shifting fallback that matches the modern-light theme.
// Rendered while a route-chunk is downloading.
const RouteFallback = () => (
  <div
    className="min-h-[60vh] flex items-center justify-center bg-white"
    role="status"
    aria-live="polite"
    aria-busy="true"
    data-testid="route-suspense"
  >
    <div className="flex flex-col items-center gap-3 text-m-muted">
      <div className="w-8 h-8 border-2 border-m-orange border-t-transparent rounded-full animate-spin" />
      <span className="text-xs tracking-[0.28em] uppercase">Laden…</span>
    </div>
  </div>
);

const S = (el) => <Suspense fallback={<RouteFallback />}>{el}</Suspense>;

function PageViewTracker() {
  const location = useLocation();
  const lastPathRef = React.useRef(null);
  useEffect(() => {
    const path = location.pathname + location.search;
    if (lastPathRef.current === path) return;  // guard against StrictMode double-fire + rapid re-renders
    lastPathRef.current = path;
    trackPageView(path);
  }, [location]);
  return null;
}

function App() {
  useEffect(() => {
    captureUtmFromLocation();
    const cleanup = initAutofillDetector();
    return cleanup;
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <ConsentProvider>
          <AuthProvider>
            <PageViewTracker />
            <Routes>
              {/* Admin routes — no public layout; lazy chunks krijgen hier
                  hun eigen Suspense-boundary (admin is nooit prerendered) */}
              <Route path="/login" element={S(<LoginPage />)} />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    {S(<AdminDashboardPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/dashboard"
                element={
                  <ProtectedRoute>
                    {S(<AdminDashboardPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/leads"
                element={
                  <ProtectedRoute>
                    {S(<AdminLeadsPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/projecten"
                element={
                  <ProtectedRoute>
                    {S(<AdminProjectsPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/projecten/:id"
                element={
                  <ProtectedRoute>
                    {S(<AdminProjectEditPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/settings"
                element={
                  <ProtectedRoute>
                    {S(<AdminSettingsPage />)}
                  </ProtectedRoute>
                }
              />

              {/* Public routes */}
              <Route element={<Layout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/configurator" element={<ConfiguratorPage />} />
                <Route path="/diensten" element={<DienstenPage />} />
                <Route path="/diensten/:slug" element={<ServicePage />} />
                <Route path="/diensten/:slug/:city" element={<ServiceCityPage />} />
                <Route path="/locaties" element={<LocatiesPage />} />
                <Route path="/locaties/:city" element={<LocationPage />} />
                <Route path="/werkgebied" element={<LocatiesPage />} />
                <Route path="/werkgebied/:slug" element={<LocationPage />} />
                <Route path="/projecten" element={<ProjectenPage />} />
                <Route path="/projecten/:slug" element={<ProjectDetailPage />} />
                <Route path="/over-ons" element={<OverOnsPage />} />
                <Route path="/over-koning" element={<Navigate to="/over-ons" replace />} />
                <Route path="/copy-of-over" element={<Navigate to="/over-ons" replace />} />
                <Route path="/reviews" element={<ReviewsPage />} />
                <Route path="/faq" element={<FaqPage />} />
                <Route path="/kennisbank" element={<KennisbankPage />} />
                <Route path="/kennisbank/:slug" element={<KennisbankArticlePage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/offerte" element={<OffertePage />} />
                <Route path="/isolatie-combi" element={<IsolatieCombiPage />} />
                <Route path="/gratis-checklist" element={<GratisChecklistPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/algemene-voorwaarden" element={<VoorwaardenPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Routes>
            <CookieConsent />
          </AuthProvider>
        </ConsentProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
